import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { findModuleForPath } from './useHelpRouteMatch';
import { HelpIndex } from './HelpIndex';
import { HelpModuleContent } from './HelpModuleContent';
import { getAllModules, type HelpModule } from '@/data/helpManual';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HelpCenter({ open, onOpenChange }: Props) {
  const { pathname } = useLocation();
  const { currentRole } = useAuth();
  const [tab, setTab] = useState<'indice' | 'contenido'>('indice');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Al abrir: detectar módulo por ruta y abrir tab Contenido si hay match.
  useEffect(() => {
    if (!open) return;
    const match = findModuleForPath(pathname, currentRole);
    if (match) {
      setSelectedId(match.module.id);
      setTab('contenido');
    } else {
      setTab('indice');
    }
  }, [open, pathname, currentRole]);

  const selected: HelpModule | null = selectedId
    ? getAllModules().find((m) => m.id === selectedId) ?? null
    : null;

  const handleSelect = (mod: HelpModule) => {
    setSelectedId(mod.id);
    setTab('contenido');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl p-0 flex flex-col gap-0"
      >
        <SheetHeader className="px-6 py-5 border-b border-border space-y-1">
          <SheetTitle className="flex items-center gap-2 text-xl">
            <HelpCircle className="w-5 h-5 text-primary" />
            Centro de Ayuda
          </SheetTitle>
          <SheetDescription>
            Manual de usuario de LIDER IA 2.0 — guía módulo por módulo.
          </SheetDescription>
        </SheetHeader>

        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as 'indice' | 'contenido')}
          className="flex-1 flex flex-col min-h-0"
        >
          <div className="px-6 pt-4">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="indice">Índice</TabsTrigger>
              <TabsTrigger value="contenido" disabled={!selected}>
                Contenido
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            <TabsContent value="indice" className="mt-0">
              <HelpIndex onSelect={handleSelect} activeModuleId={selectedId} />
            </TabsContent>
            <TabsContent value="contenido" className="mt-0">
              {selected ? (
                <HelpModuleContent
                  module={selected}
                  onBack={() => setTab('indice')}
                />
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Selecciona un módulo del índice para ver su contenido.
                </p>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
